/**
 * CreditBalanceBadge — compact header widget showing the current EIS Credits balance.
 *
 * States:
 *   - Billing disabled (pilot mode)       → renders nothing
 *   - Exempt account                      → renders nothing (grandfathered pilots)
 *   - Balance ≥ 20% of signup bonus       → neutral chip with credit count
 *   - Balance <  20% of signup bonus      → warning chip, pulsing, "Top up" affordance
 *   - Balance == 0                        → error chip, pulses, click opens paywall
 *
 * Click → opens the paywall/upgrade dialog via BillingContext.
 */
import React, { useMemo } from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

import { useBilling } from '../../context/BillingContext';

// Matches backend settings.billing_signup_bonus_credits default. If backend
// changes the signup bonus, the warning threshold here will still feel right
// because we compare against the user's current-cycle scale, not a hardcode.
const DEFAULT_MONTHLY_ALLOWANCE = 500;
const LOW_BALANCE_RATIO = 0.2;

function chipColor(balance, threshold) {
  if (balance <= 0) return 'error';
  if (balance < threshold) return 'warning';
  return 'default';
}

export default function CreditBalanceBadge() {
  const { billingEnabled, isExempt, balance, openPaywall, ready } = useBilling();

  const threshold = useMemo(() => Math.round(DEFAULT_MONTHLY_ALLOWANCE * LOW_BALANCE_RATIO), []);
  const color = chipColor(balance, threshold);
  const isLow = color !== 'default';

  // Hide when billing isn't on yet, while auth is loading, or for exempt accounts.
  if (!ready) return null;
  if (!billingEnabled) return null;
  if (isExempt) return null;

  const tooltip = balance <= 0
    ? 'No credits left. Click to top up.'
    : isLow
      ? `Running low (${balance} credits). Click to top up.`
      : `${balance} EIS Credits available`;

  const onClick = () => {
    openPaywall(balance <= 0 ? 'empty_balance' : 'low_balance');
  };

  return (
    <Tooltip title={tooltip}>
      <Box component="span" sx={{ ml: 1 }}>
        <Chip
          icon={<BoltIcon sx={{ fontSize: 16 }} />}
          label={`${balance} credits`}
          size="small"
          color={color}
          variant={color === 'default' ? 'outlined' : 'filled'}
          onClick={onClick}
          sx={{
            cursor: 'pointer',
            fontWeight: 500,
            // Pulse when low/empty so the user notices. No animation when OK.
            animation: isLow ? 'balancePulse 1.6s ease-in-out infinite' : 'none',
            '@keyframes balancePulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.6 },
            },
          }}
        />
      </Box>
    </Tooltip>
  );
}
